import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanMatchFn,
  Data,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import CryptoJS from 'crypto-js';

import { AuthService } from '../services/auth.service';

const buildForbiddenRedirect = (router: Router): UrlTree =>
  router.createUrlTree(['/forbidden']);

const buildLoginRedirect = (router: Router, returnUrl: string): UrlTree =>
  router.createUrlTree(['/login'], { queryParams: { returnUrl } });

const resolveAttemptedUrl = (segments: readonly UrlSegment[]): string => {
  const path = segments.map((segment) => segment.path).filter(Boolean).join('/');
  return path.length > 0 ? `/${path}` : '/';
};

const readRequiredModule = (data?: Data): number | undefined => {
  const moduleId = data?.['moduleId'];
  return typeof moduleId === 'number' ? moduleId : undefined;
};

const readStoredPermissions = (): readonly number[] => {
  try {
    const storedAuth = localStorage.getItem('auth');
    if (storedAuth) {
      const auth = JSON.parse(storedAuth) as { permisos?: readonly string[] | readonly number[] };
      const authPermisos = auth.permisos;
      if (Array.isArray(authPermisos)) {
        return authPermisos
          .map((permiso) => {
            if (typeof permiso === 'number') {
              return permiso;
            }

            const decrypted = CryptoJS.AES.decrypt(permiso, '11oliver11').toString(
              CryptoJS.enc.Utf8,
            );
            return Number(decrypted);
          })
          .filter((permiso): permiso is number => !Number.isNaN(permiso));
      }
    }

    const storedPermissions = localStorage.getItem('auth_permisos');
    if (!storedPermissions) {
      return [];
    }

    const parsed = JSON.parse(storedPermissions) as readonly string[];
    return parsed
      .map((permiso) => {
        const decrypted = CryptoJS.AES.decrypt(permiso, '11oliver11').toString(CryptoJS.enc.Utf8);
        return Number(decrypted);
      })
      .filter((permiso): permiso is number => !Number.isNaN(permiso));
  } catch {
    return [];
  }
};

const readRequiredPermissions = (data?: Data): readonly number[] => {
  const permissions = data?.['permissions'];
  return Array.isArray(permissions)
    ? permissions.filter((item): item is number => typeof item === 'number')
    : [];
};

/**
 * Evaluates access in three ordered layers:
 *   1. Authentication  — redirect to /login if not logged in
 *   2. Module licence  — redirect to /forbidden if data.moduleId is set and user lacks the module
 *   3. Permissions     — redirect to /forbidden if data.permissions are set and user lacks them all
 */
const evaluateAccess = (
  authService: AuthService,
  router: Router,
  requiredModule: number | undefined,
  requiredPermissions: readonly number[],
  returnUrl: string,
): boolean | UrlTree => {
  if (!authService.isLoggedIn()) {
    return buildLoginRedirect(router, returnUrl);
  }

  if (requiredModule !== undefined && !authService.hasModule(requiredModule)) {
    return buildForbiddenRedirect(router);
  }

  const storedPermissions = readStoredPermissions();
  if (
    requiredPermissions.length > 0 &&
    !requiredPermissions.some((permissionId) => storedPermissions.includes(permissionId))
  ) {
    return buildForbiddenRedirect(router);
  }

  return true;
};

export const permissionGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return evaluateAccess(
    authService,
    router,
    readRequiredModule(route.data),
    readRequiredPermissions(route.data),
    state.url,
  );
};

export const permissionMatchGuard: CanMatchFn = (
  route: Route,
  segments: UrlSegment[],
): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return evaluateAccess(
    authService,
    router,
    readRequiredModule(route.data),
    readRequiredPermissions(route.data),
    resolveAttemptedUrl(segments),
  );
};
